import { FormikProps } from 'formik';
import { QuestionFormValues } from '@/utils/forms/questionTransformers';
import { QuestionBasicFields } from './QuestionBasicFields';
import { QuestionTextFields } from './QuestionTextFields';
import { QuestionSubtextFields } from './QuestionSubtextFields';
import { QuestionImageFields } from './QuestionImageFields';
import { OverUnderConfigSection } from './OverUnderConfigSection';
import { OverUnderPlayerTeamSelection } from './OverUnderPlayerTeamSelection';
import { FixtureSelectionSection } from './FixtureSelectionSection';
import { StatTypeSelectionSection } from './StatTypeSelectionSection';
import { LeaderQuestionSection } from './LeaderQuestionSection';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { isContentLocaleEnabled } from '@/lib/platform-filters';
import { useMemo } from 'react';

interface QuestionFieldsSectionProps {
  formik: FormikProps<QuestionFormValues>;
  pickems: Array<{ pickem_id: string; title_en?: string; title_fr?: string; sport?: string }>;
  questionLeagueId?: string;
  translateField: (
    sourceKey: string,
    sourceValue: string,
    targetKey: string,
    setFieldValue: any
  ) => void;
  isFieldTranslating: (key: string) => boolean;
  isEditable: boolean;
  isNewQuestion: boolean;
  restrictedLeagueId?: string;
  selectedFixtureId?: string;
  selectedFixtureData?: {
    fixture: {
      homeTeamId?: string;
      awayTeamId?: string;
      scheduledDate?: string;
      homeTeamAbbreviation?: string;
      awayTeamAbbreviation?: string;
    };
    fixtureId: string;
  } | null;
  onFixtureSelect?: (fixtureId: string, fixture: any) => void;
  teamMap?: Map<string, { name: string; abbreviation?: string }>;
}

export function QuestionFieldsSection({
  formik,
  pickems,
  questionLeagueId,
  translateField,
  isFieldTranslating,
  isEditable,
  isNewQuestion,
  restrictedLeagueId,
  selectedFixtureId,
  selectedFixtureData,
  onFixtureSelect,
  teamMap,
}: QuestionFieldsSectionProps) {
  const { values } = formik;

  // Whether this instance authors Question text in a second Content Locale (CAE-762).
  const hasSecondaryContentLocale = isContentLocaleEnabled('fr');

  // Get pickem data to extract sport
  const selectedPickem = useMemo(() => {
    if (!values.pickem_id) return null;
    return pickems.find(p => p.pickem_id === values.pickem_id);
  }, [pickems, values.pickem_id]);

  const sport = selectedPickem?.sport;
  const leagueId = questionLeagueId;

  // Extract initial fixture date from options' grading_data.fixtureData
  // This ensures the date picker shows the correct date even if the fixture
  // is outside the default 7-day window in useQuestionFixture
  const initialFixtureDate = useMemo(() => {
    // First try to get from selectedFixtureData (if fixture was successfully loaded)
    if (selectedFixtureData?.fixture?.scheduledDate) {
      return selectedFixtureData.fixture.scheduledDate;
    }
    // Fallback: Extract from first option's grading_data.fixtureData
    const firstOptionFixtureData = values.options?.[0]?.grading_data?.fixtureData;
    if (firstOptionFixtureData?.scheduledDate) {
      return firstOptionFixtureData.scheduledDate;
    }
    return undefined;
  }, [selectedFixtureData, values.options]);

  return (
    <>
      <QuestionBasicFields
        formik={formik}
        pickems={pickems}
        isEditable={isEditable}
        isNewQuestion={isNewQuestion}
        sport={sport}
      />

      {/* Fixture Selection Section - Shows for team/player selection, over/under, and custom questions */}
      {(values.question_type === 'team' ||
        values.question_type === 'player' ||
        values.question_type === ('player_over_under' as any) ||
        values.question_type === ('team_over_under' as any) ||
        values.question_type === 'custom') &&
        isEditable && (
          <FixtureSelectionSection
            sport={sport}
            leagueId={leagueId}
            selectedFixtureId={selectedFixtureId}
            initialFixtureDate={initialFixtureDate}
            onSelect={onFixtureSelect || (() => {})}
            disabled={!isEditable}
            teamMap={teamMap}
          />
        )}

      {/* Stat Type Selection Section - Only shows when a fixture has been selected */}
      {selectedFixtureId && isEditable && (
        <StatTypeSelectionSection
          sportId={leagueId}
          questionType={values.question_type || ''}
          selectedStatTypeCode={values.stat_type_code || undefined}
          required={
            !!selectedFixtureId &&
            (values.question_type === 'player_over_under' ||
              values.question_type === 'team_over_under')
          }
          error={formik.errors.stat_type_code ? String(formik.errors.stat_type_code) : undefined}
          onSelect={statTypeCode => {
            formik.setFieldValue('stat_type_code', statTypeCode || null);
            // Sync stat type into question_metadata.grading.config.statType for over/under questions
            // so extractStatData() can find it on the frontend
            const isOverUnder =
              values.question_type === ('player_over_under' as any) ||
              values.question_type === ('team_over_under' as any) ||
              values.question_type === 'over_under';
            if (isOverUnder) {
              const config = values.question_metadata?.grading?.config || {};
              formik.setFieldValue('question_metadata.grading.config', {
                ...config,
                statType: statTypeCode || undefined,
              });
            }
          }}
          disabled={!isEditable}
        />
      )}

      {/* Authored text, per the instance's declared Content Locales (CAE-762). A tab pair
          for a single locale is a control with one destination, and implies a second
          version of the Question exists — so a one-locale instance renders the fields
          directly instead. */}
      {hasSecondaryContentLocale ? (
        <Tabs defaultValue="en" className="w-full">
          <TabsList>
            <TabsTrigger value="en">English</TabsTrigger>
            <TabsTrigger value="fr">Spanish</TabsTrigger>
          </TabsList>

          <TabsContent value="en" className="flex flex-col gap-4 mt-0">
            <QuestionTextFields
              formik={formik}
              translateField={translateField}
              isFieldTranslating={isFieldTranslating}
              isEditable={isEditable}
              language="en"
            />
            <QuestionSubtextFields
              formik={formik}
              translateField={translateField}
              isFieldTranslating={isFieldTranslating}
              isEditable={isEditable}
              language="en"
            />
            <QuestionImageFields formik={formik} isEditable={isEditable} language="en" />
          </TabsContent>

          <TabsContent value="fr" className="flex flex-col gap-4 mt-0">
            <QuestionTextFields
              formik={formik}
              translateField={translateField}
              isFieldTranslating={isFieldTranslating}
              isEditable={isEditable}
              language="fr"
            />
            <QuestionSubtextFields
              formik={formik}
              translateField={translateField}
              isFieldTranslating={isFieldTranslating}
              isEditable={isEditable}
              language="fr"
            />
            <QuestionImageFields formik={formik} isEditable={isEditable} language="fr" />
          </TabsContent>
        </Tabs>
      ) : (
        <div className="flex flex-col gap-4">
          <QuestionTextFields
            formik={formik}
            translateField={translateField}
            isFieldTranslating={isFieldTranslating}
            isEditable={isEditable}
            language="en"
          />
          <QuestionSubtextFields
            formik={formik}
            translateField={translateField}
            isFieldTranslating={isFieldTranslating}
            isEditable={isEditable}
            language="en"
          />
          <QuestionImageFields formik={formik} isEditable={isEditable} language="en" />
        </div>
      )}

      {/* Over/Under Player/Team Selection - Shows for over/under questions */}
      <OverUnderPlayerTeamSelection
        formik={formik}
        selectedTeams={
          selectedFixtureData?.fixture
            ? ([
                selectedFixtureData.fixture.homeTeamId,
                selectedFixtureData.fixture.awayTeamId,
              ].filter(Boolean) as string[])
            : []
        }
        selectedFixtureData={selectedFixtureData}
        restrictedLeagueId={restrictedLeagueId}
        isEditable={isEditable}
      />

      {/* Over/Under Configuration Section - Only shows for over_under questions */}
      <OverUnderConfigSection formik={formik} isEditable={isEditable} />

      {/* Cross-fixture leader question configuration (DKPLAT-354) — only shows for
          player_leader / team_leader. Pulls candidates from the pickem's existing
          fixture references and lets admin tick which ones to include. */}
      <LeaderQuestionSection
        formik={formik}
        pickemId={values.pickem_id}
        leagueId={leagueId}
        isEditable={isEditable}
      />
    </>
  );
}
